/**
 * Application state and the wiring between the transport and the screens.
 *
 * There is one store and it holds only what more than one screen needs: the
 * route, the chosen data source, the projection stream, and the two preferences
 * §16.5 requires the user to control. Screen-local data (a run's stages, a
 * spend table) is fetched by the screen that shows it, so a fetch failure on
 * one screen cannot blank another — §16.3 asks for a *source-specific* error,
 * which is only possible if failures stay attached to their source.
 *
 * The store also owns the `CursorTracker`. §16.4 is precise that the cursor
 * tracked is the last frame *rendered*, so `markRendered` is called by the
 * screen after it paints, not here when the frame arrives.
 */

import { CursorTracker, type OfficeScene, type StreamFrame } from '@autonomous-team/ui-projections';

import { createApiDataSource, type ApiOptions } from './client/api.js';
import { decodeScene, type ConsoleDataSource, type OfficeReadClient, type Subscription } from './client/data-source.js';
import { createOfficeReadClient } from './client/office-client.js';
import { consoleIdToken } from './firebase-auth.js';
import { failed, loading, ready, type LoadState } from './logic/view-phase.js';
import {
  acknowledgeDiscard,
  applyFrame,
  INITIAL_STREAM_STATE,
  markConnecting,
  markError,
  planResume,
  type StreamState,
} from './logic/stream-state.js';



export type SourceKind = 'live';

/**
 * Which palette to use.
 *
 * `system` is the default and follows the operating system, because someone
 * watching a queue at three in the morning did not choose dark mode for fun.
 * The other two are an explicit override for the case the system gets wrong —
 * a bright room on a laptop set to dark, most often.
 */
export type ThemeChoice = 'system' | 'light' | 'dark';

const THEME_KEY = 'autonomous-team.theme';

function isThemeChoice(value: string | null): value is ThemeChoice {
  return value === 'system' || value === 'light' || value === 'dark';
}

/** Reads the stored choice. Falls back to `system` on anything unexpected. */
export function readTheme(): ThemeChoice {
  try {
    const stored = globalThis.localStorage?.getItem(THEME_KEY) ?? null;
    return isThemeChoice(stored) ? stored : 'system';
  } catch {
    // Storage can be unavailable (private mode, a locked-down profile). A theme
    // is not worth failing a page load over.
    return 'system';
  }
}

/**
 * Applies the choice to the document and remembers it.
 *
 * `system` removes the attribute rather than writing "system", so the CSS has
 * one condition to express — an explicit override — instead of two.
 */
export function applyTheme(choice: ThemeChoice): void {
  const root = globalThis.document?.documentElement;
  if (root === undefined) return;
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.dataset['theme'] = choice;

  /*
   * Primer reads its own attributes: color-mode picks which of the two
   * loaded theme files answers, and the light/dark theme names pin the
   * palettes. `auto` follows the OS exactly like our own light-dark() does.
   */
  root.dataset['colorMode'] = choice === 'system' ? 'auto' : choice;
  root.dataset['lightTheme'] = 'light';
  root.dataset['darkTheme'] = 'dark';

  try {
    globalThis.localStorage?.setItem(THEME_KEY, choice);
  } catch {
    // The theme still applies for this session; it just will not be remembered.
  }
}

/**
 * What one agent has produced recently, assembled from the live stream.
 *
 * Bounded on purpose. This is a window on to work in progress, not the record:
 * the authoritative output is the `assistant_message` written to the logical
 * thread when the attempt settles, and the Agents screen links to it.
 */
export interface LiveTranscript {
  readonly roleInstanceId: string;
  readonly attemptId: string | null;
  /** Everything relayed so far for the current attempt, trimmed from the front. */
  readonly text: string;
  /**
   * Just the most recent slice.
   *
   * Kept separately so the screen can render it as the part that has *just*
   * arrived. Without it the pane is a block of text that silently grows, which
   * is indistinguishable from a static block that was already there.
   */
  readonly newestSlice: string;
  readonly updatedAtMs: number;
  /**
   * True when a slice sequence was skipped.
   *
   * §16.4: a client must never present a gap as continuous. The text is still
   * shown — it is real — but the screen says a piece is missing rather than
   * letting the two halves read as one sentence.
   */
  readonly gap: boolean;
}

/** Kept per role. Older text is dropped from the front. */
const TRANSCRIPT_LIMIT = 4_000;

export interface AppState {
  readonly sourceKind: SourceKind;
  readonly stream: StreamState;
  readonly scene: LoadState<OfficeScene>;
  /** Text pushed into the shell's `aria-live` region (§16.5). */
  readonly announcement: string;
  /** User override on top of `prefers-reduced-motion`; the media query still wins when it is set. */
  readonly reduceMotion: boolean;
  readonly theme: ThemeChoice;
  /** The team every request means (§multi-team); defaults to the served one. */
  readonly currentTeamId: string;
  /** Live model output, keyed by role instance id (ADR-114). */
  readonly transcripts: ReadonlyMap<string, LiveTranscript>;
}

const TEAM_KEY = 'autonomous-team.team';

function readTeamChoice(): string | null {
  try {
    return globalThis.localStorage?.getItem(TEAM_KEY) ?? null;
  } catch {
    return null;
  }
}

export interface ConsoleConfig {
  readonly baseUrl: string;
  readonly actorId: string;
  readonly teamId: string;
  /** The team's name, for the header. `null` when no team is active. */
  readonly teamName: string | null;
  /** Loopback session token, injected by the daemon that served this page. */
  readonly sessionToken: string | null;
  readonly csrfToken: string | null;
  /** Query-scoped token for the Office projection; cannot perform a mutation. */
  readonly officeToken: string | null;
  readonly firebase: FirebaseConsoleConfig | null;
}

export interface FirebaseConsoleConfig {
  readonly apiKey: string;
  readonly authDomain: string;
  readonly projectId: string;
  readonly appId: string;
}

export type Listener = (state: AppState) => void;

export class Store {
  #state: AppState;
  #listeners = new Set<Listener>();
  #config: ConsoleConfig;
  #dataSource: ConsoleDataSource;
  readonly isDemo: boolean;
  #injectedDataSource: ConsoleDataSource | undefined;
  #officeClient: OfficeReadClient;
  #cursors = new CursorTracker();
  /** Last slice sequence seen per role, for gap detection. */
  #lastSequence = new Map<string, number>();
  #subscription: Subscription | null = null;

  constructor(config: ConsoleConfig, initial: { sourceKind?: SourceKind; dataSource?: ConsoleDataSource; demo?: boolean } = {}) {
    this.#config = config;
    this.isDemo = initial.demo ?? false;
    this.#injectedDataSource = initial.dataSource;
    const sourceKind: SourceKind = initial.sourceKind ?? 'live';

    this.#state = {
      sourceKind,
      stream: INITIAL_STREAM_STATE,
      scene: loading<OfficeScene>('office projection'),
      announcement: '',
      reduceMotion: false,
      theme: readTheme(),
      currentTeamId: this.isDemo ? this.#config.teamId : readTeamChoice() ?? this.#config.teamId,
      transcripts: new Map(),
    };
    this.#dataSource = this.#createDataSource();
    this.#officeClient = this.#createOfficeClient();
  }

  get state(): AppState {
    return this.#state;
  }

  get data(): ConsoleDataSource {
    return this.#dataSource;
  }

  /**
   * The Office's client.
   *
   * Typed as `OfficeReadClient`, which has no mutation method at all. See
   * `client/office-client.ts` for why that type exists (ADR-005, AC-031).
   */
  get office(): OfficeReadClient {
    return this.#officeClient;
  }

  get config(): ConsoleConfig {
    return this.#config;
  }

  subscribe(listener: Listener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #emit(next: Partial<AppState>): void {
    this.#state = { ...this.#state, ...next };
    for (const listener of this.#listeners) {
      listener(this.#state);
    }
  }

  setReduceMotion(reduceMotion: boolean): void {
    this.#emit({ reduceMotion });
  }

  setTheme(theme: ThemeChoice): void {
    applyTheme(theme);
    this.#emit({ theme });
  }

  /** The team every request means right now — the switcher's choice. */
  get currentTeamId(): string {
    return this.#state.currentTeamId;
  }

  /** Switch every screen to another team; remembered across sessions. */
  setTeam(currentTeamId: string): void {
    if (currentTeamId === this.#state.currentTeamId) return;
    try {
      if(!this.isDemo) globalThis.localStorage?.setItem(TEAM_KEY, currentTeamId);
    } catch {
      // Remembering is a nicety; switching still works for this session.
    }
    if(this.isDemo && typeof location !== 'undefined'){const url=new URL(location.href);url.searchParams.set('scenario',currentTeamId);history.replaceState(null,'',url);}
    this.#emit({ currentTeamId });
    /*
     * The projection stream is a per-team subscription (§multi-team): the old
     * socket carries the old team's scene, and a cursor into it names a place
     * in that team's story. Cold-start the stream so the first thing painted
     * for the new team is its own complete snapshot.
     */
    this.stopStream();
    this.#cursors.reset();
    this.#emit({ stream: INITIAL_STREAM_STATE, scene: loading<OfficeScene>('office projection') });
    this.startStream();
  }

  announce(announcement: string): void {
    if (announcement === this.#state.announcement) return;
    this.#emit({ announcement });
  }

  /** Called by the renderer once a frame has actually been painted (§16.4). */
  markRendered(cursor: string): void {
    this.#cursors.markRendered(cursor);
  }

  /** Clears the one-shot "drop your cached positions" flag. */
  acknowledgeDiscard(): void {
    const stream = acknowledgeDiscard(this.#state.stream);
    if (stream !== this.#state.stream) {
      this.#emit({ stream });
    }
  }

  /**
   * Folds an `attempt.output` frame into the per-role transcript (ADR-114).
   *
   * Returns `null` for every other frame so the common path allocates nothing.
   *
   * A new attempt replaces the text rather than appending to it: two attempts on
   * one role are two separate pieces of work, and running them together on
   * screen would read as one long thought that never happened.
   */
  #applyOutput(frame: StreamFrame): ReadonlyMap<string, LiveTranscript> | null {
    if (frame.kind !== 'event' || frame.eventType !== 'attempt.output') return null;

    const envelope = frame.payload as
      | { attempt?: { id?: unknown } | null; payload?: Record<string, unknown> }
      | null;
    const body = envelope?.payload ?? null;
    if (body === null) return null;

    const roleInstanceId = typeof body['roleInstanceId'] === 'string' ? body['roleInstanceId'] : null;
    const text = typeof body['text'] === 'string' ? body['text'] : null;
    if (roleInstanceId === null || text === null) return null;

    const sequence = typeof body['sequence'] === 'number' ? body['sequence'] : null;
    const attemptId = typeof envelope?.attempt?.id === 'string' ? envelope.attempt.id : null;

    const previous = this.#state.transcripts.get(roleInstanceId) ?? null;
    const sameAttempt = previous !== null && previous.attemptId === attemptId;
    const expected = sameAttempt ? this.#lastSequence.get(roleInstanceId) : null;
    const gap = (sameAttempt && previous.gap) || (expected !== null && expected !== undefined && sequence !== null && sequence !== expected + 1);

    if (sequence !== null) this.#lastSequence.set(roleInstanceId, sequence);

    const joined = sameAttempt ? previous.text + text : text;
    const next = new Map(this.#state.transcripts);
    next.set(roleInstanceId, {
      roleInstanceId,
      attemptId,
      // Trimmed from the front: the newest text is the part being read.
      text: joined.length > TRANSCRIPT_LIMIT ? joined.slice(joined.length - TRANSCRIPT_LIMIT) : joined,
      newestSlice: text,
      updatedAtMs: Date.now(),
      gap,
    });
    return next;
  }

  #createDataSource(): ConsoleDataSource {
    if(this.#injectedDataSource) return this.#injectedDataSource;
    const options: ApiOptions = {
      baseUrl: this.#config.baseUrl,
      actorId: this.#config.actorId,
      teamId: this.#config.teamId,
      currentTeamId: () => this.#state.currentTeamId,
      sessionToken: this.#config.sessionToken,
      csrfToken: this.#config.csrfToken,
      firebaseToken: this.#config.firebase === null ? undefined : consoleIdToken,
    };
    return createApiDataSource(options);
  }

  #createOfficeClient(): OfficeReadClient {
    if(this.#injectedDataSource) return this.#injectedDataSource;
    // Built by `createOfficeReadClient`, which issues nothing but GET (ADR-005).
    return createOfficeReadClient({
          baseUrl: this.#config.baseUrl,
          resumeCursor: () => this.#cursors.cursor,
          sessionToken: this.#config.officeToken,
          firebaseToken: this.#config.firebase === null ? undefined : consoleIdToken,
          currentTeamId: () => this.#state.currentTeamId,
        });
  }

  #rebuildSource(): void {
    this.stopStream();
    this.#cursors.reset();
    this.#dataSource = this.#createDataSource();
    this.#officeClient = this.#createOfficeClient();
    this.#emit({ stream: INITIAL_STREAM_STATE, scene: loading<OfficeScene>('office projection') });
    this.startStream();
  }

  /**
   * Opens the projection stream.
   *
   * A cold start always takes a snapshot first: there is no state to preserve,
   * and starting from a snapshot means the first thing rendered is a complete
   * scene rather than a partially-applied stream of deltas.
   */
  async refreshDemoScene():Promise<void> {
    if(!this.isDemo)return;
    const snapshot=await this.#officeClient.sceneSnapshot();
    this.#emit({scene:{status:'ready',value:snapshot.scene,receivedAt:Date.now()}});
  }

  startStream(): void {
    if (this.#subscription !== null) return;
    this.#emit({ stream: markConnecting(this.#state.stream) });

    /*
     * The `catch` covers the fetch and nothing else.
     *
     * It used to wrap the `emit` too, and `emit` paints synchronously — so any
     * exception thrown by any component during that paint was caught here and
     * reported as "office projection: <render error>". An unrelated panel
     * crashing on an undefined field therefore presented as a broken 2D scene,
     * and the banner named the wrong subsystem while the real stack was only in
     * the browser console. Misattributed errors are worse than none: they send
     * whoever reads them to the wrong file.
     */
    void this.#officeClient.sceneSnapshot().then(
      (snapshot) => {
        this.#emit({
          scene: ready(snapshot.scene, Date.now()),
          stream: {
            ...this.#state.stream,
            status: 'live',
            scene: snapshot.scene,
            cursor: snapshot.scene.cursor,
            discardScene: true,
            replayWindow: snapshot.replayWindow,
          },
        });
        if (snapshot.scene.cursor !== null) {
          this.#cursors.markRendered(snapshot.scene.cursor);
        }
      },
      (cause: unknown) => {
        this.#emit({
          scene: failed<OfficeScene>('office projection', describeError(cause), true),
          stream: markError(this.#state.stream, describeError(cause)),
        });
      },
    );

    this.#subscription = this.#officeClient.subscribeScene(this.#cursors.cursor, {
      onOpen: () => {
        this.#emit({ stream: { ...this.#state.stream, status: 'live', notice: null } });
      },
      onFrame: (frame) => {
        const transcripts = this.#applyOutput(frame);
        const stream = applyFrame(this.#state.stream, frame, Date.now(), decodeScene);
        const scene: LoadState<OfficeScene> =
          stream.scene === null
            ? stream.status === 'error'
              ? failed<OfficeScene>('office projection', stream.notice ?? 'The projection stream failed.', true)
              : loading<OfficeScene>('office projection')
            : ready(stream.scene, Date.now());
        this.#emit(transcripts === null ? { stream, scene } : { stream, scene, transcripts });
      },
      onError: (message) => {
        // Decide the resume *before* reconnecting, so a cursor that has aged out
        // discards the scene instead of being animated across (ADR-107).
        const { next } = planResume(markError(this.#state.stream, message));
        if (next.cursor === null) {
          this.#cursors.reset();
        }
        this.#emit({
          stream: next,
          scene:
            next.scene === null
              ? failed<OfficeScene>('office projection', next.notice ?? message, true)
              : this.#state.scene,
        });
      },
    });
  }

  stopStream(): void {
    this.#subscription?.close();
    this.#subscription = null;
  }

  /** Retry control behind the §16.3 Error row. */
  retry(): void {
    this.stopStream();
    this.#emit({ scene: loading<OfficeScene>('office projection'), stream: markConnecting(this.#state.stream) });
    this.startStream();
  }
}

export function describeError(cause: unknown): string {
  if (cause instanceof Error) return cause.message;
  return String(cause);
}

/** Reads the config block that the Control API injects into `index.html`. */
export function readConfig(): ConsoleConfig {
  const node = document.getElementById('console-config');
  const fallback: ConsoleConfig = {
    baseUrl: '',
    actorId: 'local-operator',
    teamId: 'team-product-engineering',
    teamName: null,
    sessionToken: null,
    csrfToken: null,
    officeToken: null,
    firebase: null,
  };
  if (node === null) return fallback;
  try {
    const parsed = JSON.parse(node.textContent ?? '{}') as Partial<ConsoleConfig>;
    return {
      baseUrl: parsed.baseUrl ?? fallback.baseUrl,
      actorId: parsed.actorId ?? fallback.actorId,
      teamId: parsed.teamId ?? fallback.teamId,
      teamName: parsed.teamName ?? null,
      // Absent when the Console is opened from a static server rather than
      // served by the daemon. Every read then fails with `missing_session`,
      // which is the correct and legible outcome — the alternative is an
      // unauthenticated control plane.
      sessionToken: parsed.sessionToken ?? null,
      csrfToken: parsed.csrfToken ?? null,
      officeToken: parsed.officeToken ?? null,
      firebase: parsed.firebase ?? null,
    };
  } catch {
    return fallback;
  }
}
